import React, { useState, useEffect } from 'react';
import { PostRequest, GetRequest } from 'components/common/index.js';
import { ContentPopup } from 'components/ContentPopup.jsx';
import { selectedBranch } from 'components/quote_builder_widget/ProductImagesTree';
import styles from 'components/quote_builder_widget/settings/_style.module.scss';

const translate = (key, options) => t(`quote_builder_product_images.${key}`, options);

// The owner's product images: a menu entry of the widgets page, whose tree selects the branch shown here, and a tab of
// the widget settings.
const ProductImages = ({ onChange, onCountChange, onListChange, selection }) => {
  const [mainGroups, setMainGroups] = useState([]);
  const [hasAWidget, setHasAWidget] = useState(false);
  const [maximumPerDrawing, setMaximumPerDrawing] = useState();
  const [maximumFileSizeInMegabytes, setMaximumFileSizeInMegabytes] = useState();
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [showAddPopup, setShowAddPopup] = useState(false);
  const [catalogName, setCatalogName] = useState('');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadErrorMessage, setUploadErrorMessage] = useState('');
  const [dragging, setDragging] = useState(false);
  const [filePreviewUrl, setFilePreviewUrl] = useState(null);

  useEffect(() => {
    if (!file) {
      setFilePreviewUrl(null);
      return undefined;
    }
    const url = URL.createObjectURL(file);
    setFilePreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const fetchProductImages = (uploadedCatalogName) => {
    GetRequest({
      url: '/quote-builder/product-images/list',
      callback: (data) => {
        setMainGroups(data.product_images);
        setHasAWidget(data.has_a_widget);
        setMaximumPerDrawing(data.maximum_per_drawing);
        setMaximumFileSizeInMegabytes(data.maximum_file_size_in_megabytes);
        setLoading(false);
        onCountChange(data.images_count);
        onListChange(data.product_images, uploadedCatalogName);
      },
      onError: () => {
        setLoading(false);
        setErrorMessage(translate('failed_to_load'));
      }
    });
  };

  useEffect(() => { fetchProductImages(); }, []);

  const afterChange = (uploadedCatalogName) => {
    fetchProductImages(uploadedCatalogName);
    if (onChange) {
      onChange();
    }
  };

  // PostRequest rejects after its status handler, and a network failure reaches only this catch.
  const post = ({ url, data, callback, setError }) => {
    const showError = (response) => {
      setError((response && response.error) || translate('server_error'));
    };
    setError('');
    return PostRequest({ url, data, callback, 400: showError, 404: showError, 422: showError, 500: showError })
      .catch(() => {
        setError((previous) => previous || translate('server_error'));
      });
  };

  const openAddPopup = () => {
    setCatalogName((selection && selection.catalogName) || '');
    setFile(null);
    setDragging(false);
    setUploadErrorMessage('');
    setShowAddPopup(true);
  };

  const closeAddPopup = () => {
    setShowAddPopup(false);
  };

  // The answer names the catalog name as the drawing has it, whatever capitals it was typed in.
  const upload = () => {
    setUploading(true);
    post({
      url: '/quote-builder/product-images',
      data: { file, catalog_name: catalogName.trim(), file_fields: ['file'] },
      callback: (data) => {
        closeAddPopup();
        afterChange(data.catalog_name);
      },
      setError: setUploadErrorMessage,
    }).finally(() => {
      setUploading(false);
    });
  };

  const move = (image, direction) => {
    post({ url: `/quote-builder/product-images/${image.id}/move`, data: { direction }, callback: () => afterChange(), setError: setErrorMessage });
  };

  const remove = (image) => {
    if (!confirm(translate('delete_confirmation'))) {
      return;
    }
    post({ url: `/quote-builder/product-images/${image.id}/delete`, callback: () => afterChange(), setError: setErrorMessage });
  };

  const iconButton = ({ label, icon, className, disabled, onClick }) => (
    <button type="button" className={`dct-button dct-button--small ${className}`} title={label} aria-label={label} disabled={disabled} onClick={onClick}>
      <i className={`fa ${icon}`} aria-hidden="true"></i>
    </button>
  );

  const renderImage = (image, index, images) => (
    <div key={image.id} className={styles.productImageTile} data-testid="product-image">
      <img src={image.url} alt={t('quote_builder_widget.product_image', { number: index + 1 })} className={styles.productImageThumbnail} />
      <div className={styles.productImageActions}>
        {iconButton({ label: translate('move_left'), icon: 'fa-arrow-left', className: 'dct-button--secondary', disabled: index === 0, onClick: () => move(image, 'left') })}
        {iconButton({ label: translate('move_right'), icon: 'fa-arrow-right', className: 'dct-button--secondary', disabled: index === images.length - 1, onClick: () => move(image, 'right') })}
        {iconButton({ label: translate('delete'), icon: 'fa-trash', className: 'dct-button--danger', onClick: () => remove(image) })}
      </div>
    </div>
  );

  const renderCatalogNameEntry = (entry) => (
    <div key={entry.catalog_name} className={styles.addedCard} data-testid="product-images-catalog-name">
      <div className={styles.productImagesCardHeader}>
        <div className={styles.addedCardTitle}>{entry.catalog_name}</div>
        <div className={styles.addedCardDetails}>{translate('added_count', { added: entry.images.length, maximum: maximumPerDrawing })}</div>
      </div>
      <div className={styles.productImageGrid}>
        {entry.images.map((image, index) => renderImage(image, index, entry.images))}
      </div>
    </div>
  );

  const chooseFile = (chosenFile) => {
    setFile(chosenFile || null);
    setUploadErrorMessage('');
  };

  const renderChosenFile = () => (
    <div className={styles.productImageChosenFile}>
      <img src={filePreviewUrl} alt="" className={styles.productImageChosenFileThumbnail} />
      <div className={styles.productImageChosenFileName}>
        <div>{file.name}</div>
        <div className={styles.helpText} style={{ margin: 0 }}>{`${(file.size / 1024 / 1024).toFixed(1)} MB`}</div>
      </div>
      <label htmlFor="product-images-file" className="dct-button dct-button--secondary dct-button--small" style={{ margin: 0 }}>
        {translate('change_image')}
      </label>
    </div>
  );

  const renderDropArea = () => (
    <label
      htmlFor="product-images-file"
      className={dragging ? `${styles.productImageDropArea} ${styles.productImageDropAreaDragging}` : styles.productImageDropArea}
      onDragOver={(event) => {
        event.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(event) => {
        event.preventDefault();
        setDragging(false);
        chooseFile(event.dataTransfer.files[0]);
      }}
      data-testid="product-image-drop-area"
    >
      <i className={`fa fa-cloud-upload ${styles.productImageDropAreaIcon}`} aria-hidden="true"></i>
      <span>{translate('drop_image')}</span>
      <span className={styles.helpText} style={{ margin: 0 }}>{translate('file_types', { maximum_size: maximumFileSizeInMegabytes })}</span>
    </label>
  );

  const renderAddPopupContent = () => (
    <div className={styles.productImagesPopup}>
      <h4 className={styles.productImagesPopupTitle}>{translate('add_product_image')}</h4>

      <label className={styles.label} htmlFor="product-images-catalog-name">{translate('catalog_name')}</label>
      <input
        id="product-images-catalog-name"
        type="text"
        className="form-control"
        placeholder={translate('catalog_name_placeholder')}
        value={catalogName}
        onChange={(event) => setCatalogName(event.target.value)}
      />
      <p className={styles.helpText} style={{ marginTop: '6px' }}>{translate('catalog_name_information')}</p>

      <div className={styles.label} style={{ marginTop: '16px' }}>{translate('image')}</div>
      <input
        id="product-images-file"
        type="file"
        accept="image/png,image/jpeg,image/webp"
        className={styles.productImageFileInput}
        onChange={(event) => chooseFile(event.target.files[0])}
        data-testid="product-image-file"
      />
      {file ? renderChosenFile() : renderDropArea()}

      {uploadErrorMessage !== '' && (
        <div className={styles.errorText} role="alert" style={{ marginTop: '12px' }}>{uploadErrorMessage}</div>
      )}

      <div className={styles.productImagesPopupActions}>
        <button type="button" className="dct-button dct-button--secondary" onClick={closeAddPopup}>
          {translate('cancel')}
        </button>
        <button
          type="button"
          className="dct-button dct-button--primary"
          disabled={uploading || catalogName.trim() === '' || !file}
          onClick={upload}
        >
          <i className="fa fa-upload" aria-hidden="true"></i> {translate(uploading ? 'uploading' : 'upload')}
        </button>
      </div>
    </div>
  );

  if (loading) {
    return <div style={{ padding: '10px', color: '#666' }}>{t('quote_builder_widget.loading')}</div>;
  }

  return (
    <div>
      <p className={styles.helpText}>{translate('information', { maximum: maximumPerDrawing })}</p>
      {hasAWidget ? (
        <button type="button" className="dct-button dct-button--primary" onClick={openAddPopup}>
          <i className="fa fa-plus-square-o" aria-hidden="true"></i> {translate('add_product_image')}
        </button>
      ) : (
        <p className={styles.installationWarning} data-testid="product-images-widget-required">{translate('widget_required_information')}</p>
      )}

      {errorMessage !== '' && (
        <div className={styles.errorText} role="alert" style={{ marginTop: '10px' }}>{errorMessage}</div>
      )}

      <div style={{ marginTop: '20px' }}>
        {mainGroups.length === 0 ? (
          <p style={{ color: '#666', fontStyle: 'italic' }}>{translate('empty')}</p>
        ) : (
          selectedBranch(mainGroups, selection)
            .flatMap((mainGroup) => mainGroup.groups)
            .flatMap((group) => group.catalog_name_entries)
            .map(renderCatalogNameEntry)
        )}
      </div>

      {showAddPopup && (
        <ContentPopup
          autoOpen={true}
          notWide={true}
          modalStyle={{ width: '560px', maxWidth: 'calc(100% - 32px)' }}
          content={renderAddPopupContent()}
          cancelButton={false}
          beforeClose={() => setShowAddPopup(false)}
        />
      )}
    </div>
  );
};

export default ProductImages;
